import React, { useEffect, useRef, useState } from 'react';
import { arrayOf, bool, func, node, object, string } from 'prop-types';

import debounce from 'lodash/debounce';
import classNames from 'classnames';

import FilterPlain from '../FilterPlain/FilterPlain';
import FilterPopup from '../FilterPopup/FilterPopup';
import FieldSelectIntegerRange from './FieldSelectIntegerRange';

import { FormattedMessage } from '../../../util/reactIntl';
import css from './IntegerRangeFilter.module.css';

const RADIX = 10;

// Takes a single argument `valueRange`, expected to be a string that contains two numbers
// separated by a comma, e.g: "0,1000". It extracts an object containing the keys `minValue` and `maxValue`
// or null if the input is not valid. This function is used to convert query parameters into an object.
const convertQueryParamToObject = valueRange => {
  const [minValue, maxValue] = !!valueRange
    ? valueRange.split(',').map(v => Number.parseInt(v, RADIX))
    : [];
  // We explicitly compare `minValue` to null as comparing to '0' returns false but '0' is a valid range value.
  return !!valueRange && minValue != null && maxValue != null ? { minValue, maxValue } : null;
};

// Transforms a range object containing `minValue` and `maxValue`
// into query parameter format, i.e. "0,1000"
const formatToQueryParam = (range, queryParamName) => {
  const { minValue, maxValue } = range || {};
  // We explicitly compare `minValue` to null as comparing to '0' returns false but '0' is a valid range value.
  const value = minValue != null && maxValue != null ? `${minValue},${maxValue}` : null;
  return { [queryParamName]: value };
};

const IntegerRangeFilter = props => {
  const {
    min,
    max,
    step,
    onSubmit,
    queryParamNames,
    initialValues,
    label,
    rootClassName,
    className,
    intl,
    id,
    name,
    showAsPopup,
    onValidityChange: externalOnValidityChange,
    registerFlush,
    ...rest
  } = props;

  const classes = classNames(rootClassName || css.root, className);

  // While the min/max inputs hold an invalid (out-of-bounds or crossed) pair,
  // the popup's Apply button should be disabled rather than let bad values through.
  const [hasRangeError, setHasRangeError] = useState(false);

  // Popup mode disables its own Apply button via `hasRangeError`. Bottom-sheet
  // mode (Flash Search mobile) has no per-field footer, so it reports up to
  // SearchFiltersMobile's single "Aplicar filtros" button via this callback.
  const handleValidityChange = isInvalid => {
    setHasRangeError(isInvalid);
    if (externalOnValidityChange) {
      externalOnValidityChange(isInvalid);
    }
  };

  const hasValue = value => value != null;

  // Extracts initial values for form from query parameters. If a valid query parameter exists, it converts
  // the string to an object using `convertQueryParamToObject`. If not, initializes as an empty object.
  const parsedInitialValues =
    initialValues && initialValues[queryParamNames]
      ? convertQueryParamToObject(initialValues[queryParamNames])
      : {};

  const { minValue, maxValue } = parsedInitialValues || {};
  const hasInitialValues = initialValues && hasValue(minValue) && hasValue(maxValue);

  const resolvedInitialValues = { [name]: hasInitialValues ? parsedInitialValues : {} };

  // Always mirrors exactly what's currently shown in the min/max boxes
  // (updated by FieldSelectIntegerRange on every keystroke), independent of
  // final-form's own tracked value — which can otherwise get silently reset
  // by a spurious reinitialize while the user is still typing. Cleared
  // explicitly on Clear/Cancel (see below) so those still discard the draft
  // instead of applying it.
  const latestFieldValuesRef = useRef(null);
  const clearLiveValues = () => {
    latestFieldValuesRef.current = null;
  };

  // Handles form submission by extracting and updating the range values from the form's current state.
  const handleSubmit = values => {
    const rawValue = values?.[name] ? values[name] : values;
    const usedValue = latestFieldValuesRef.current || rawValue;
    const { minValue, maxValue } = usedValue || {};

    // The full default span (e.g. 1-5000) is equivalent to "no filter" —
    // don't write it to the URL, so it isn't counted as an active filter
    // (popup highlight, mobile nav badge, selected-filters count, etc.)
    // on either the desktop popup or the mobile bottom sheet.
    const isFullDefaultRange = minValue === min && maxValue === max;

    return onSubmit(
      formatToQueryParam(
        isFullDefaultRange ? {} : { minValue, maxValue },
        queryParamNames
      )
    );
  };

  // We use useRef to create a mutable object that persists across renders without causing re-renders.
  // This is used as a flag to control whether debouncing should be applied.
  // Debouncing should only be applied when liveEdit is `true`, i.e. in the grid view, mobile view
  // or in the secondary filter view.
  const bypassDebounce = useRef(false);

  // Always points at the latest `handleSubmit` closure, so the debounced
  // wrapper below can stay a single stable instance across renders instead
  // of being recreated (and losing its pending timer) every keystroke/drag.
  const handleSubmitRef = useRef(handleSubmit);
  handleSubmitRef.current = handleSubmit;

  // A debounced version of the `handleSubmit` function to prevent excessive
  // or premature submissions during subsequent and rapid input changes.
  // Created once via useRef (not per-render) so it can be flushed on demand
  // — see `registerFlush` below, needed by the mobile bottom sheet's
  // "Aplicar filtros" button, which otherwise can read stale pending state.
  const debouncedSubmitRef = useRef(
    debounce(
      values => {
        // Check if debounce should be bypassed
        if (bypassDebounce.current) {
          bypassDebounce.current = false;
          return;
        }
        handleSubmitRef.current(values);
      },
      400,
      { leading: false, trailing: true }
    )
  );
  const handleSubmitOnLive = debouncedSubmitRef.current;

  // Lets a parent (the mobile bottom sheet) force any pending debounced
  // submit to apply immediately before it reads/pushes filter state — e.g.
  // when the user resets the range to its default and taps "Aplicar
  // filtros" before the 400ms debounce would otherwise have fired.
  useEffect(() => {
    if (!registerFlush) return undefined;
    registerFlush(() => debouncedSubmitRef.current.flush());
    return () => registerFlush(null);
  }, [registerFlush]);

  // If we don't have a specific function here, there will be a delay when pressing clear
  const handleClear = values => {
    // Sets the bypass flag to true, instructing the debounced handleSubmit to skip its next invocation.
    bypassDebounce.current = true;
    clearLiveValues();
    handleSubmit(null);
  };

  // Used to display the selected values above the filter component in the "grid" view
  const labelSelectionForPlain = hasInitialValues ? (
    <FormattedMessage id="IntegerRangeFilter.labelSelectedPlain" values={{ minValue, maxValue }} />
  ) : null;

  return showAsPopup ? (
    <FilterPopup
      className={classes}
      rootClassName={rootClassName}
      label={label}
      isSelected={hasInitialValues}
      id={`${id}.popup`}
      onSubmit={handleSubmit}
      onClear={clearLiveValues}
      onCancel={clearLiveValues}
      initialValues={resolvedInitialValues}
      disableSubmit={hasRangeError}
      {...rest}
    >
      <FieldSelectIntegerRange
        max={max}
        min={min}
        name={name}
        step={step}
        initialValues={resolvedInitialValues}
        onValidityChange={handleValidityChange}
        onValuesChange={values => {
          latestFieldValuesRef.current = values;
        }}
      />
    </FilterPopup>
  ) : (
    <FilterPlain
      className={classes}
      rootClassName={rootClassName}
      label={label}
      labelSelection={labelSelectionForPlain}
      labelSelectionSeparator=":"
      isSelected={hasInitialValues}
      id={`${id}.plain`}
      liveEdit
      onClear={handleClear}
      onSubmit={handleSubmitOnLive}
      initialValues={resolvedInitialValues}
      {...rest}
    >
      <FieldSelectIntegerRange
        isInSideBar
        max={max}
        min={min}
        name={name}
        step={step}
        initialValues={resolvedInitialValues}
        onValidityChange={handleValidityChange}
        onValuesChange={values => {
          latestFieldValuesRef.current = values;
        }}
      />
    </FilterPlain>
  );
};

IntegerRangeFilter.defaultProps = {
  rootClassName: null,
  className: null,
  showAsPopup: true,
  liveEdit: false,
  initialValues: null,
  onValidityChange: null,
  registerFlush: null,
};

IntegerRangeFilter.propTypes = {
  rootClassName: string,
  className: string,
  id: string.isRequired,
  label: node,
  liveEdit: bool,
  onValidityChange: func,
  registerFlush: func,
  queryParamNames: arrayOf(string).isRequired,
  onSubmit: func.isRequired,
  initialValues: object,
  showAsPopup: bool,
};

export default IntegerRangeFilter;
